import React from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  Lightbulb, 
  PersonCheck, 
  Award, 
  Bullseye, 
  CheckCircle,
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin 
} from 'react-bootstrap-icons';
import whiteLogo from '../assets/whitebglogo.png';
import androidQrCode from '../assets/playstoreqr.png';
import iosQrCode from '../assets/iosqrcode.png';
import SEOHead from './SEOHead';
import '../App.css';

const AboutUs = () => {
  const navigate = useNavigate();

  const handleStartLearning = () => {
    window.open('https://cubegon.app', '_blank');
  };

  const handleExploreResources = () => {
    navigate('/');
    // Scroll to classes section after navigation
    setTimeout(() => {
      const classesSection = document.getElementById('classes-section');
      if (classesSection) {
        classesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  const features = [
    {
      title: 'Comprehensive Study Materials',
      description: 'Access 120+ recorded lectures, 70+ comprehensive notes, and unlimited practice tests aligned with CBSE curriculum.'
    },
    {
      title: 'AI-Powered Learning',
      description: 'Three powerful AI modes: Doubt Solver for instant help, Teach Back for self-evaluation, and Analogy Mode for better understanding.'
    },
    {
      title: 'Gamified Learning Experience',
      description: 'Earn XP, maintain streaks, compete on leaderboards, and unlock rewards as you progress through your learning journey.'
    },
    {
      title: 'Flexible Access',
      description: 'Available on both mobile app and web dashboard with 24/7 access to all learning materials and AI features.'
    },
    {
      title: 'Rewards & Recognition',
      description: 'Redeem points for exciting gifts and compete for monthly leaderboard rewards with top 3 students winning grand prizes.'
    },
    {
      title: 'Progress Tracking',
      description: 'Monitor your learning journey with detailed analytics, XP tracking, streak monitoring, and performance insights.'
    }
  ];

  const values = [
    {
      title: 'Quality Education',
      description: 'We believe in providing high-quality, comprehensive educational resources that help students excel.'
    },
    {
      title: 'Accessibility',
      description: 'Making quality education accessible to students across India, regardless of their location or background.'
    },
    {
      title: 'Innovation',
      description: 'Continuously innovating our platform with the latest educational technologies and methodologies.'
    },
    {
      title: 'Student Success',
      description: 'Our primary focus is on student success and helping them achieve their academic goals.'
    }
  ];

  const stats = [
    { number: '120+', label: 'Video Lectures' },
    { number: '70+', label: 'Study Notes' },
    { number: '3', label: 'AI Learning Modes' },
    { number: '24/7', label: 'Access Available' }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Cubegon - CBSE Resources",
    "description": "Learn about Cubegon's mission to revolutionize education through gamified learning, AI-powered tools, and comprehensive CBSE-aligned content.",
    "url": "https://cbseresources.com/about",
    "mainEntity": {
      "@type": "EducationalOrganization",
      "name": "Cubegon",
      "description": "India's leading gamified learning platform that helps school students master their subjects while earning rewards",
      "url": "https://cbseresources.com",
      "logo": "https://cbseresources.com/favicon.ico",
      "sameAs": [
        "https://facebook.com/cubegon",
        "https://twitter.com/cubegon",
        "https://instagram.com/cubegon.app",
        "https://linkedin.com/company/cubegon"
      ]
    }
  };

  return (
    <div className="about-us-page">
      <SEOHead
        title="About Cubegon - CBSE Resources | Gamified Learning Platform"
        description="Learn about Cubegon's mission to revolutionize education through gamified learning, AI-powered tools, and comprehensive CBSE-aligned content for classes 9-12."
        keywords="about cubegon, CBSE resources, gamified learning, AI education, CBSE study materials, educational technology, India education"
        canonical="/about"
        structuredData={structuredData}
      />
      <Container fluid className="px-4 px-lg-5">
        {/* Hero Section */}
        <section className="about-hero-section">
          <Container>
            <div className="about-hero-card">
              <div className="about-hero-content">
                <Badge className="about-badge mb-3">About Cubegon</Badge>
                <h1 className="about-hero-title">Empowering Students Through Quality Education</h1>
                <p className="about-hero-subtitle">
                  Cubegon is India's leading gamified learning platform that helps school students master their 
                  subjects while earning rewards. Available on both mobile app and web dashboard.
                </p>
                <p className="about-hero-description">
                  Founded with the vision of making learning engaging and rewarding, Cubegon combines education 
                  with entertainment through AI-powered tools, comprehensive CBSE-aligned content, and a 
                  gamified experience that motivates students to learn smarter and achieve more.
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* Mission & Vision Section */}
        <section className="mission-vision-section">
          <Container>
            <Row className="g-4">
              <Col md={6}>
                <Card className="mission-card h-100">
                  <Card.Body>
                    <div className="mission-icon">
                      <Bullseye className="mission-icon-svg" />
                    </div>
                    <h3 className="mission-title">Our Mission</h3>
                    <p className="mission-text">
                      To revolutionize education by combining learning with rewards, making studying 
                      engaging and motivating through gamification, AI-powered tools, and comprehensive 
                      CBSE-aligned content that helps students excel while having fun.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="vision-card h-100">
                  <Card.Body>
                    <div className="vision-icon">
                      <Lightbulb className="vision-icon-svg" />
                    </div>
                    <h3 className="vision-title">Our Vision</h3>
                    <p className="vision-text">
                      To become India's most innovative gamified learning platform, where students 
                      don't just study but earn rewards, compete with peers, and develop a genuine 
                      love for learning through AI-powered personalized education experiences.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <Container>
            <h2 className="section-title">Why Choose Cubegon?</h2>
            <Row className="g-4">
              {features.map((feature, index) => (
                <Col md={6} lg={4} key={index}>
                  <Card className="feature-card h-100">
                    <Card.Body>
                      <h4 className="feature-title">{feature.title}</h4>
                      <p className="feature-description">{feature.description}</p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <Container>
            <h2 className="section-title">Our Impact</h2>
            <Row className="g-4">
              {stats.map((stat, index) => (
                <Col md={6} lg={3} key={index}>
                  <Card className="stat-card">
                    <Card.Body className="text-center">
                      <h3 className="stat-number">{stat.number}</h3>
                      <p className="stat-label">{stat.label}</p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <Container>
            <h2 className="section-title">Our Core Values</h2>
            <Row className="g-4">
              {values.map((value, index) => (
                <Col md={6} lg={3} key={index}>
                  <Card className="value-card h-100">
                    <Card.Body>
                      <div className="value-icon">
                        <CheckCircle className="value-icon-svg" />
                      </div>
                      <h4 className="value-title">{value.title}</h4>
                      <p className="value-description">{value.description}</p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Team Section */}
        <section className="team-section">
          <Container>
            <h2 className="section-title">Our Commitment</h2>
            <Row>
              <Col lg={8} className="mx-auto">
                <Card className="team-card">
                  <Card.Body className="text-center">
                    <div className="team-icon">
                      <PersonCheck className="team-icon-svg" />
                    </div>
                    <h3 className="team-title">Innovation-Driven Team</h3>
                    <p className="team-description">
                      Our team consists of experienced educators, AI specialists, and technology professionals 
                      who are passionate about revolutionizing education. We combine pedagogical expertise with 
                      cutting-edge technology to create engaging, gamified learning experiences that motivate 
                      students to excel.
                    </p>
                    <p className="team-description">
                      From AI engineers developing intelligent learning modes to content creators crafting 
                      CBSE-aligned materials, our diverse team works together to ensure every student has 
                      access to world-class education that's both effective and enjoyable.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Call to Action Section */}
        <section className="cta-section">
          <Container>
            <Card className="cta-card">
              <Card.Body className="text-center">
                <Award className="cta-icon mb-3" />
                <h3 className="cta-title">Start Learning & Earning Today</h3>
                <p className="cta-description">
                  Join thousands of students who are learning smarter and earning rewards with Cubegon's 
                  gamified platform. Access AI-powered tools, comprehensive content, and exciting rewards.
                </p>
                <div className="cta-buttons">
                  <button 
                    className="btn btn-primary btn-lg me-3"
                    onClick={handleStartLearning}
                  >
                    Start Learning Now
                  </button>
                  <button 
                    className="btn btn-outline-primary btn-lg"
                    onClick={handleExploreResources}
                  >
                    Explore Resources
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </section>
      </Container>

      {/* Footer */}
      <footer className="cubegon-footer">
        <div className="cubegon-footer-content">
          <div className="cubegon-footer-main">
            <div className="cubegon-footer-logo">
              <img src={whiteLogo} alt="Cubegon" />
            </div>

            <div className="cubegon-footer-download">
              <h3>Download the App</h3>
              <div className="cubegon-footer-qr-container">
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.cubegon.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={androidQrCode} alt="Android QR code - Download from Play Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>Android</p>
                  </a>
                </div>
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://apps.apple.com/in/app/cubegon-smart-learning/id6752596017" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={iosQrCode} alt="iOS QR code - Download from App Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>iOS</p>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="cubegon-footer-bottom">
            <div className="cubegon-footer-social">
              <a aria-label="Facebook" href="https://facebook.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Facebook />
              </a>
              <a aria-label="Twitter" href="https://twitter.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Twitter />
              </a>
              <a aria-label="Instagram" href="https://instagram.com/cubegon.app" target="_blank" rel="noopener noreferrer">
                <Instagram />
              </a>
              <a aria-label="LinkedIn" href="https://linkedin.com/company/cubegon" target="_blank" rel="noopener noreferrer">
                <Linkedin />
              </a>
            </div>

            <p className="cubegon-footer-text">Made in 🇮🇳 with ❤️</p>
            <p className="cubegon-footer-copyright">© {new Date().getFullYear()} Numeric Education Pvt. Ltd. All rights reserved.</p>
            <div className="cubegon-footer-links">
              <a href="https://cubegon.app/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://cubegon.app/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
